// The asset page (asset-detail-revamp-handover): the asset's tags for the Asset tag card, and its life — one paged
// feed from public.asset_lifecycle, which runs as the caller, so deployments and the purchase reach managers only.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export interface AssetTag {
  id: string;
  type: 'ble_beacon' | 'ble_sensor' | 'gps_tracker' | 'qr' | 'nfc';
  identifier: string;
  status: string;
  attached_at: string | null;
  mounting_photo_url: string | null;
}

export const ELECTRONIC_TAG_TYPES = ['ble_beacon', 'ble_sensor', 'gps_tracker'] as const;

export function useAssetTags(assetId: string) {
  return useQuery({
    queryKey: ['asset-tags', assetId],
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase()
        .from('tags')
        .select('id, type, identifier, status, attached_at, mounting_photo_url')
        .eq('asset_id', assetId)
        .order('attached_at', { ascending: false, nullsFirst: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as AssetTag[];
    },
  });
}

export type LifecycleKind =
  | 'registered'
  | 'purchased'
  | 'tag_attached'
  | 'location'
  | 'transfer_dispatched'
  | 'transfer_received'
  | 'deployment_started'
  | 'deployment_ended'
  | 'zone'
  | 'counted'
  | 'retired'
  | 'tag_removed'
  // CR_20260921_Asset_Lifecycle T1
  | 'certificate_added'
  | 'dispatch_override'
  // CR_20260921_Asset_Lifecycle T2
  | 'condition'
  | 'out_of_service'
  | 'back_in_service'
  // CR_20260921_Asset_Lifecycle T3
  | 'serviced';

export interface LifecycleItem {
  at: string;
  kind: LifecycleKind;
  /** stable per row; with `at` it is the page cursor */
  key: string;
  source: string | null;
  params: Record<string, string | number | boolean | null>;
}

export const lifecycleKeys = { asset: (assetId: string) => ['asset-lifecycle', assetId] as const };

/** Newest first, `pageSize` at a time; fetchNextPage continues from the last row's (at, key). */
export function useAssetLifecycle(assetId: string, pageSize: number) {
  return useInfiniteQuery({
    queryKey: [...lifecycleKeys.asset(assetId), pageSize],
    enabled: !!assetId,
    initialPageParam: null as { at: string; key: string } | null,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await supabase().rpc('asset_lifecycle', {
        p_asset_id: assetId,
        p_before: pageParam?.at ?? null,
        p_limit: pageSize,
        p_before_key: pageParam?.key ?? null,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as LifecycleItem[];
    },
    getNextPageParam: (last) =>
      last.length === pageSize ? { at: last.at(-1)!.at, key: last.at(-1)!.key } : undefined,
  });
}
